import { webStaticProps } from '@/web-app/utils/window'
import type { ICurrentWebUser } from '@/web-app/api/users'
import { createContext, useCallback, useContext, useEffect, useRef } from 'react'
import { useQueryUrl } from '@/shared/hooks/use-query-url'
import { useQueryClient } from '@tanstack/react-query'

interface ICurrentWebUserProviderProps {
  children: React.ReactNode
}

interface ICurrentWebUserProviderState {
  currentUser: Nullable<ICurrentWebUser>
  isLoading: boolean
  refetch: () => Promise<void>
}

const initialState: ICurrentWebUserProviderState = {
  currentUser: null,
  isLoading: true,
  refetch: async () => {},
}

const CurrentWebUserProviderContext = createContext<ICurrentWebUserProviderState>(initialState)

export function CurrentWebUserProvider({ children, ...props }: ICurrentWebUserProviderProps) {
  const { currentUserUrl } = webStaticProps()
  const queryClient = useQueryClient()
  const { data: currentUser, refetch: queryRefetch, isLoading } = useQueryUrl<ICurrentWebUser>(currentUserUrl)
  const previousCurrentUserIdRef = useRef<Nullable<number>>(null)

  useEffect(() => {
    if (isLoading) return

    // Only clear if users is changing from authenticated to unauthenticated
    if (previousCurrentUserIdRef.current) {
      // If the user is no longer logged in, or otherwise changed, clear the query client cache
      if (!currentUser || (previousCurrentUserIdRef.current && currentUser.id !== previousCurrentUserIdRef.current)) {
        queryClient.clear()
      }
    }
    previousCurrentUserIdRef.current = currentUser?.id
  }, [isLoading, currentUser, queryClient])

  const refetch = useCallback(async () => {
    await queryRefetch()
  }, [queryRefetch])

  const value = {
    currentUser,
    refetch,
    isLoading,
  }

  return (
    <CurrentWebUserProviderContext.Provider {...props} value={value}>
      {children}
    </CurrentWebUserProviderContext.Provider>
  )
}

export const useCurrentWebUser = () => {
  const context = useContext(CurrentWebUserProviderContext)

  return context
}
